<template>
<FormKitLazyProvider config-file="true">

  <FormKit
    v-if="isFormLoaded"
    ref="formKit"
    v-model="formData"
    type="form"
    :submit-label="computedSubmitLabel"
    :input-errors="inputErrors"
    :form-class="formClass"
    @submit="onSubmit"
    @click="handleFieldClick"
  >
    <FormKitSchema :schema="schema" :data="schemaData" />
    <div aria-live="assertive">
      <FormKitMessages />
    </div>
  </FormKit>

</FormKitLazyProvider>
</template>

<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import { FormKitSchema, FormKitMessages } from '@formkit/vue'
import type { FormKitSchemaNode } from '@formkit/core'
import type { MutationVariables } from './../../../app/types/webform'
import { populateFormData } from './../../helpers/webformMapper'
import { mapFormData } from './../../helpers/mapFormData'
import { asWebformItem } from './../../helpers/asWebformItem'
import { getSchemaBuilder } from './../../helpers/mappers'
import type {
  WebformFragment,
  WebformElementNestedFragment,
  WebformSubmissionResultFragment,
} from '#graphql-operations'
import { filterNullArray } from '~/helpers'

export type WebformSubmitHandler = (
  formData: MutationVariables,
) => Promise<WebformSubmissionResultFragment>

const emit = defineEmits<{
  (
    e: 'success',
    data: { token?: string; id?: string; redirectUrl?: string },
  ): void
}>()

const props = defineProps<{
  form: WebformFragment['form']
  submitForm: WebformSubmitHandler
  submitLabel?: string
  formClass: string | string[]
  settings?: WebformFragment['settings']
  mapping?: {
    urls?: {
      termsOfServiceUrl?: string
    }
  }
}>()

const { buildChildren } = await getSchemaBuilder({
  termsOfServiceUrl: props.mapping?.urls?.termsOfServiceUrl,
})

const formKit = ref(null)
const isFormLoaded = ref(false)

const webformItems = computed(() =>
  (props.form?.elements || [])
    .filter(
      (element): element is WebformElementNestedFragment => element !== null,
    )
    .map(asWebformItem),
)

const schema = computed<FormKitSchemaNode[]>(() => {
  return buildChildren(webformItems.value)
})

const formData = ref(populateFormData(webformItems.value, {}))

const isChecked = (fieldValue: unknown, conditionValue: string): boolean => {
  if (Array.isArray(fieldValue)) {
    return fieldValue.includes(conditionValue)
  }
  return !!fieldValue
}

const schemaData = computed(() => ({
  ...formData.value,
  isChecked,
}))

const inputErrors = ref<Record<string, string[]>>({})

const { $texts } = useEasyTexts()

const computedSubmitLabel = computed(() => {
  if (props.submitLabel) {
    return props.submitLabel
  }
  for (let i = 0; i < webformItems.value.length; i++) {
    const item = webformItems.value[i]
    if (item && item.element?.__typename === 'WebformElementWebformActions') {
      const submitLabel =
        'submitLabel' in item.element ? item.element.submitLabel : undefined
      if (submitLabel) {
        return submitLabel
      }
    }
  }

  return $texts('submit')
})

const handleFieldClick = (event: MouseEvent) => {
  if (!(event.target instanceof HTMLElement)) {
    return
  }

  const outer = event.target.closest('.formkit-outer')
  if (!(outer instanceof HTMLElement)) {
    return
  }

  const input = outer.querySelector('input')

  if (input && typeof input.showPicker === 'function') {
    try {
      input.showPicker()
    } catch {
      // Noop.
    }
  }
}

/* eslint-disable  @typescript-eslint/no-explicit-any */
async function onSubmit(data: any) {
  try {
    const result = await props.submitForm!(mapFormData(data))
    inputErrors.value = (result?.validationErrors || []).reduce<
      Record<string, string[]>
    >((acc, v) => {
      if (v?.element && v.messages) {
        acc[v.element] = filterNullArray(v.messages)
      }
      return acc
    }, {})
    if (result.errors?.length || result.validationErrors?.length) {
      return
    }
    emit('success', {
      id: result.submission?.id?.toString(),
      token: result.submission?.token,
      redirectUrl: result.submission?.confirmation?.redirectUrl,
    })
  } catch (e) {
    console.log(e)
  }
}

onMounted(() => {
  isFormLoaded.value = true
})
</script>
